import * as React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import { t, tc } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

import * as modalAction from '../../../../actions/modal'
import { useReward } from '../hooks/useReward'
import * as projectModel from '../../../models/project'
import { RootState } from '../../../../reducers'
import * as modalReducer from '../../../../reducers/modals/rewardModal'
import { TRACKING_SOURCE } from '../utils/trackerEnums'
import * as URLs from '../../../utils/URLs'
import { BaseReward } from './Rewards/BaseReward'

const RewardList = styled.div`
  display: flex;
  justify-content: center;
`

export const RewardModal = (): React.ReactElement<unknown> => {
  const dispatch = Redux.useDispatch()

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const modal = Redux.useSelector<RootState, modalReducer.State>((state) => state.application.rewardModal)
  const { rewards } = project
  const sharedRewardId = modal.rewardId

  const [reward, setReward] = React.useState<models.reward.Reward<projectModel.ExtraFields>>()

  const { handleClick } = useReward()
  const trackingSource =
    reward?.is_hidden || !reward?.available ? TRACKING_SOURCE.REWARD_MODAL : TRACKING_SOURCE.PROJECT_BACKERS_LISTING

  React.useEffect(() => {
    if (sharedRewardId && rewards) {
      const selectedReward = rewards.find((projectReward) => {
        return projectReward.id === parseInt(sharedRewardId)
      })
      if (selectedReward) {
        const modeledSelectedReward = models.reward.Reward<projectModel.ExtraFields>(selectedReward, project)
        setReward(modeledSelectedReward)
      }
    }
  }, [sharedRewardId])

  React.useEffect(() => {
    // HACK: On iOS < 14, Safari scrolls the main document body instead of scrolling modal contents.
    // To counter that, as a quick'n'dirty solution, we add a `no-scroll` class to the body, in order to be able to hide its contents when a modal is open.
    const body = document.body

    if (modal.open) {
      body.classList.add('no-scroll')
    } else {
      body.classList.remove('no-scroll')
    }
  }, [modal.open])

  return reward ? (
    <StyledModal
      closable
      open={modal.open}
      onClose={() => {
        dispatch(modalAction.toggleModalReward(false))
        dispatch(modalAction.setRewardId(''))
      }}
    >
      {reward.is_hidden && reward.available ? (
        <TextContainer>
          <plume.styles.heading.L>{t('A reward just for you!')}</plume.styles.heading.L>
          <plume.styles.copy.S.regular>
            {tc('%(ownername)s has granted you access to a secret reward, would you like to have it?', {
              ownername: models.user.fullName(project.owner),
            })}
          </plume.styles.copy.S.regular>
        </TextContainer>
      ) : (
        !reward.available && (
          <TextContainer>
            <plume.styles.heading.L>{t("You're too late...")}</plume.styles.heading.L>
            <plume.styles.copy.S.regular>
              {t('This reward is already sold out, but there are plenty more!')}
            </plume.styles.copy.S.regular>
          </TextContainer>
        )
      )}
      <RewardList>
        <BaseReward
          reward={reward}
          onClick={(id, optionValuesIds) => {
            handleClick(id, { optionValuesIds, trackingExtraData: { source: trackingSource } })
          }}
        />
      </RewardList>

      <ActionsWrapper>
        {!models.reward.hasChildren(reward) && (
          <>
            {!reward.available ? (
              <StyledCallToAction
                onClick={() => {
                  if (!models.project.isOrdersEnabled<projectModel.ExtraFields>(project)) {
                    dispatch(modalAction.toggleModalPaymentDisturbance(true))
                  } else {
                    window.location.href = URLs.getCheckoutURL(project.id)
                  }
                }}
              >
                {t('See available rewards')}
              </StyledCallToAction>
            ) : (
              <StyledCallToAction
                onClick={() => {
                  handleClick(reward.id, { trackingExtraData: { source: trackingSource } })
                }}
              >
                {t('Choose this reward')}
              </StyledCallToAction>
            )}
          </>
        )}
        <plume.Link
          kind="secondary"
          as="a"
          onClick={(event) => {
            event.preventDefault()
            dispatch(modalAction.toggleModalReward(false))
            dispatch(modalAction.setRewardId(''))
          }}
          href="#"
        >
          {t('Close')}
        </plume.Link>
      </ActionsWrapper>
    </StyledModal>
  ) : (
    <></>
  )
}

const ActionsWrapper = styled.div`
  margin: 17px auto 0;
  text-align: center;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 361px;
  }
`

const StyledModal = styled(plume.Modal)`
  ${plume.styles.modal.Body} {
    margin-top: 20px;
  }

  // HACK: On iOS < 14, Safari scrolls the main document body instead of scrolling modal contents.
  // As a partial counter, we enlarge modal contents and reset the overflow on Apple devices.
  @supports (-webkit-overflow-scrolling: touch) {
    ${plume.styles.modal.Body} {
      max-height: 70vh;
      overflow: auto;
    }
  }
`

const StyledCallToAction = styled(plume.styles.button.CallToAction)`
  margin-bottom: 8px;
`

const TextContainer = styled.div`
  text-align: center;
  margin-bottom: 35px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    width: 361px;
    display: block;
    margin: 0 auto;
    margin-bottom: 35px;
  }

  ${plume.styles.heading.L} {
    margin-bottom: 10px;
    display: block;
    text-align: center;
  }
`

export default RewardModal
